// Risk tier constants and helpers shared across the HAB dashboard

export const RISK_TIERS = {
  BASELINE: { key: 'BASELINE', label: 'Baseline', plain: 'Safe to use',     color: '#2f6b46', bg: '#e9f1ea', border: '#cfe3d4', solid: '#4f9d6b', tier: 1 },
  ELEVATED: { key: 'ELEVATED', label: 'Elevated', plain: 'Use caution',     color: '#8a6308', bg: '#faf2db', border: '#ecdcae', solid: '#c08a1e', tier: 2 },
  ADVISORY: { key: 'ADVISORY', label: 'Advisory', plain: 'Limit contact',   color: '#a8500f', bg: '#f8e9dd', border: '#eccbab', solid: '#cc6716', tier: 3 },
  WARNING:  { key: 'WARNING',  label: 'Warning',  plain: 'Avoid the water', color: '#9e2b25', bg: '#f7e3e0', border: '#eac3bd', solid: '#bd3a32', tier: 4 },
};

export const TIER_ORDER = ['BASELINE', 'ELEVATED', 'ADVISORY', 'WARNING'];
export const TIER_COLORS = ['#4f9d6b', '#c08a1e', '#cc6716', '#bd3a32'];

// cyanobacteria_index 0 → BASELINE, 1 → ELEVATED, 2 → ADVISORY, 3 → WARNING
export const indexToTier = (index) => {
  const i = Math.max(0, Math.min(3, Math.round(Number(index) || 0)));
  return TIER_ORDER[i];
};

export const PROB_LINE = {
  BASELINE: 'Estimated <25% chance of exceeding safety guidelines',
  ELEVATED: 'Estimated 25–75% chance of exceeding safety guidelines',
  ADVISORY: 'Estimated >75% chance of exceeding safety guidelines',
  WARNING:  'Estimated >90% chance of exceeding safety guidelines',
};

export const getHeroHeadline = (tierKey, pcTrend) => {
  if (tierKey === 'ELEVATED' && pcTrend === 'rising') return 'A bloom may be developing';
  const map = {
    BASELINE: 'Lake conditions look good',
    ELEVATED: 'Cyanobacteria levels are elevated',
    ADVISORY: 'Significant bloom — avoid water contact',
    WARNING:  'Stay out of the water',
  };
  return map[tierKey] || map.BASELINE;
};

const GUIDANCE = {
  BASELINE: {
    adults: "Conditions look good. Enjoy the water — just don't swallow lake water, and rinse off afterward. As always, if anything looks or smells off when you get there, trust your senses.",
    kids:   "Kids can likely swim normally. Supervise them and remind them not to drink lake water. If the water looks green or smells musty when you arrive, pick another activity.",
    pets:   "Dogs can likely swim. Bring fresh drinking water from home so they're not drinking lake water, and hose them off after — dogs are far more sensitive to these toxins than people.",
  },
  ELEVATED: {
    adults_rising: "A bloom may be building. Brief swimming may still be okay if the water looks clear at your location, but conditions are changing — check back before going out again. Look carefully for any green tinge or surface streaks before getting in.",
    adults_other:  "Cyanobacteria elevated. If the water looks and smells normal at your spot, brief swimming with precautions is reasonable. Rinse off thoroughly after. Look for any green color or musty smell before getting in.",
    kids_rising:   "Think twice. Conditions are changing. If you let them swim, keep it brief and watch the water closely for any green color or surface streaks.",
    kids_other:    "If the water looks completely clear, closely supervised wading may be okay. If anything looks green, cloudy, or smells musty, find another activity.",
    pets:          "Keep dogs out and bring fresh drinking water from home. Dogs are the most vulnerable — they drink lake water, lick their fur, and can't tell you they feel sick.",
  },
  ADVISORY: {
    adults: "Avoid getting in the water. If you see scum or green water at any shoreline, keep your distance. Boating with minimal splash may be acceptable, but avoid kayaking or activities close to the water surface.",
    kids:   "Keep children out and away from the shoreline. Young children are especially vulnerable — they swallow more water and have lower body weight.",
    pets:   "Keep all pets completely away from water and shoreline. Do not allow any water contact or drinking.",
  },
  WARNING: {
    adults: "Stay away from the water. Don't swim, wade, boat, or fish. Avoid breathing spray or mist near shore. If you see scum, keep well back from the shoreline.",
    kids:   "Keep children completely away from water and shoreline. If accidental contact occurs, rinse immediately with clean water and watch for symptoms.",
    pets:   "DANGER — cyanotoxins can be fatal to dogs within hours. Keep all animals completely away. If exposed, rinse immediately and call your vet.",
  },
};

export const getGuidance = (tierKey, population, pcTrend) => {
  const g = GUIDANCE[tierKey];
  if (!g) return '';
  if (tierKey === 'ELEVATED') {
    if (population === 'adults') return pcTrend === 'rising' ? g.adults_rising : g.adults_other;
    if (population === 'kids')   return pcTrend === 'rising' ? g.kids_rising   : g.kids_other;
    return g.pets;
  }
  if (population === 'adults') return g.adults;
  if (population === 'kids')   return g.kids;
  return g.pets;
};

export const computePcTrend = (transformedData) => {
  const data = transformedData?.pc_fluorometer?.length
    ? transformedData.pc_fluorometer
    : (transformedData?.pc || []);
  if (data.length < 2) return 'stable';
  const now = Date.now();
  const sixHrMs = 6 * 3600000;
  const recent = data.filter(r => now - new Date(r.time) < sixHrMs);
  const prior  = data.filter(r => { const age = now - new Date(r.time); return age >= sixHrMs && age < 12 * 3600000; });
  if (!recent.length || !prior.length) return 'stable';
  const avg = (arr) => arr.reduce((s, r) => s + r.sensor_value, 0) / arr.length;
  const pct = (avg(recent) - avg(prior)) / Math.max(Math.abs(avg(prior)), 0.01);
  return pct > 0.1 ? 'rising' : pct < -0.1 ? 'declining' : 'stable';
};

export const computeSevenDayTrend = (transformedData) => {
  const data = transformedData?.cyanobacteria_index || [];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (6 - i));
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    const readings = data.filter(r => { const t = new Date(r.time); return t >= d && t < next; });
    const maxVal = readings.length ? Math.max(...readings.map(r => r.sensor_value)) : null;
    return {
      day: i === 6 ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' }),
      tierKey: maxVal !== null ? indexToTier(maxVal) : null,
    };
  });
};

export const computeCurrentIndicators = (transformedData) => {
  if (!Object.keys(transformedData || {}).length) return null;
  const sixHr = new Date(Date.now() - 6 * 3600000);
  const avg = (key) => {
    const arr = (transformedData[key] || []).filter(r => new Date(r.time) >= sixHr);
    return arr.length ? arr.reduce((s, r) => s + r.sensor_value, 0) / arr.length : null;
  };
  const tempC = transformedData.temperature?.[0]?.sensor_value ?? null;
  // Use the corrected RDO temperature (fall back to raw RDO if unavailable).
  const tempRdoC = avg('temperature_rdo_corrected') ?? avg('temperature_rdo');
  return {
    pc:             avg('pc'),
    pc_fluorometer: avg('pc_fluorometer'),
    chla:           avg('chla'),
    temperature:    tempC != null ? tempC * 9 / 5 + 32 : null,
    temperature_rdo: tempRdoC != null ? tempRdoC * 9 / 5 + 32 : null,
    o2_mg:          (transformedData.o2_25?.[0]?.sensor_value ?? 0) * 0.032,
    o2_rdo:         avg('o2_rdo'),
    turbidity:      avg('turbidity_fluorometer'),
    cyanobacteriaIndex: transformedData.cyanobacteria_index?.[0]?.sensor_value ?? 0,
  };
};
